import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Target Tracker — FTUE demo (3 essais progressive intro).
 *
 * Spec : wiki/games/target-tracker.md §FTUE
 *   E1 : 1 distracteur (2 orbes), pursuit lent — la cible évidente.
 *   E2 : 2 distracteurs, vitesse normale — les imposteurs entrent.
 *   E3 : 3 distracteurs — calibration baseline.
 *
 * MVP : positions statiques, identification après highlight (pas de pursuit
 * continu). TODO Sprint 2+ : animation pointermove.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'target_tracker.demo.step1_title',
        hintKey: 'target_tracker.demo.step1_hint',
        nDistractors: 1,
        highlightMs: 1100,
        trackingMs: 3500,
    },
    {
        idx: 2,
        titleKey: 'target_tracker.demo.step2_title',
        hintKey: 'target_tracker.demo.step2_hint',
        nDistractors: 2,
        highlightMs: 900,
        trackingMs: 4000,
    },
    {
        idx: 3,
        titleKey: 'target_tracker.demo.step3_title',
        hintKey: 'target_tracker.demo.step3_hint',
        nDistractors: 3,
        highlightMs: 800,
        trackingMs: 4500,
    },
];
const FIELD = 240;
const ORB_R = 14;
const ORB_MARGIN = 24;
function makeDemoPositions(nOrbs) {
    const cx = FIELD / 2;
    const cy = FIELD / 2;
    const radius = (FIELD - ORB_MARGIN * 2) / 2;
    const out = [];
    for (let i = 0; i < nOrbs; i += 1) {
        const angle = (i / nOrbs) * Math.PI * 2 - Math.PI / 2;
        const jitter = (Math.random() - 0.5) * 14;
        const r = radius + jitter;
        out.push({
            idx: i,
            x: cx + Math.cos(angle) * r,
            y: cy + Math.sin(angle) * r,
        });
    }
    return out;
}
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('tracking');
    const [highlightVisible, setHighlightVisible] = useState(true);
    const [pickedIdx, setPickedIdx] = useState(null);
    const positionsRef = useRef([]);
    const trueIdxRef = useRef(0);
    const highlightTimerRef = useRef(null);
    const trackingTimerRef = useRef(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const correct = pickedIdx === trueIdxRef.current;
    useEffect(() => {
        if (phase !== 'tracking')
            return;
        const nOrbs = step.nDistractors + 1;
        positionsRef.current = makeDemoPositions(nOrbs);
        trueIdxRef.current = Math.floor(Math.random() * nOrbs);
        setHighlightVisible(true);
        highlightTimerRef.current = setTimeout(() => {
            setHighlightVisible(false);
        }, step.highlightMs);
        trackingTimerRef.current = setTimeout(() => {
            setPhase('awaiting');
        }, step.trackingMs);
        return () => {
            if (highlightTimerRef.current)
                clearTimeout(highlightTimerRef.current);
            if (trackingTimerRef.current)
                clearTimeout(trackingTimerRef.current);
        };
    }, [phase, step]);
    const handlePick = (idx) => {
        if (phase !== 'awaiting')
            return;
        setPickedIdx(idx);
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('tracking');
        setPickedIdx(null);
    };
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('target_tracker.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsxs("div", { className: "relative bg-cyan-network", style: { width: FIELD, height: FIELD }, children: [(phase === 'tracking' || phase === 'awaiting') &&
                        positionsRef.current.map((p) => {
                            const highlighted = phase === 'tracking' && highlightVisible && p.idx === trueIdxRef.current;
                            const baseStyle = {
                                left: p.x - ORB_R,
                                top: p.y - ORB_R,
                                width: ORB_R * 2,
                                height: ORB_R * 2,
                                background: '#f5f1e8',
                            };
                            if (phase === 'awaiting') {
                                return (_jsx("button", { type: "button", onClick: () => handlePick(p.idx), "aria-label": `Orbe ${p.idx + 1}`, className: "absolute rounded-full transition duration-feedback hover:scale-110 active:scale-95", style: baseStyle }, p.idx));
                            }
                            return (_jsx("div", { "aria-hidden": true, className: [
                                    'absolute rounded-full transition duration-feedback',
                                    highlighted
                                        ? 'ring-4 ring-cyan-core'
                                        : '',
                                ].join(' '), style: baseStyle }, p.idx));
                        }), phase === 'feedback' && (_jsx("div", { className: "flex h-full w-full items-center justify-center", children: _jsx("span", { className: [
                                'font-display text-[30px]',
                                correct ? 'text-cyan-core' : 'text-cyan-media',
                            ].join(' '), children: correct ? '✓' : '✗' }) }))] }), phase === 'feedback' && (_jsx("p", { className: [
                    'text-[13px]',
                    correct ? 'text-cyan-text' : 'text-cyan-media',
                ].join(' '), children: correct
                    ? t('target_tracker.demo.feedback_correct')
                    : t('target_tracker.demo.feedback_incorrect') })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map